import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, TextField } from '@/components/ui';
import { cn } from '@/utils/cn';
import type { Contact } from './content';

interface ContactBlockProps {
  contact: Contact;
}

/**
 * Single office / affiliate contact block. Used inside the Contact us header
 * and inside each regional row (Asia / Europe / Americas).
 *
 * Typography matches the our story page conventions:
 *   - Name (label)         : SemiBold 16/18 clearlab-midnight
 *   - Email                : Button / Transparent / Large / Blue —
 *                            renders as 16/44 clearlab-blue with the standard
 *                            animated underline on hover/focus.
 *   - Address              : Regular 16/44 clearlab-dusk (one <p> per line)
 *   - Tel / Fax row        : Regular 16/44, label clearlab-steel `w-10` (40px)
 *                             + 24px gap + value clearlab-midnight
 *
 * Inner gap-2 (8px) between name and the content cluster matches `AboutBlock`.
 * Inside the content cluster, gap-6 (24px) separates email / address / numbers.
 */
export function ContactBlock({ contact }: ContactBlockProps) {
  const { t } = useTranslation();
  const numberLabel = (type: 'Tel' | 'Fax') =>
    type === 'Tel' ? t('contact.contactNumber.tel') : t('contact.contactNumber.fax');
  // Address is sourced from translations (`contact.addresses.<key>`) so each
  // language can override only its own office's address — Korean for the
  // Korea office under `ko`, Spanish for Spain under `es`, German for
  // Germany under `de`. All other offices fall back to English.
  const addressLines = contact.addressKey
    ? (t(`contact.addresses.${contact.addressKey}`, { returnObjects: true }) as string[])
    : undefined;
  return (
    <div className="flex w-full flex-col items-start gap-2">
      <p className="text-[16px] font-semibold leading-[18px] text-clearlab-midnight">
        {contact.name}
      </p>
      <div className="flex w-full flex-col items-start gap-6">
        <Button
          href={contact.email.href}
          variant="transparent"
          size="large"
          tone="blue"
          className="p-0 leading-[44px]"
        >
          {contact.email.label}
        </Button>
        {addressLines && addressLines.length > 0 ? (
          <div className="flex w-full flex-col">
            {addressLines.map((line) => (
              <p
                key={line}
                className="w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight"
              >
                {line}
              </p>
            ))}
          </div>
        ) : null}
        <div className="flex w-full flex-col items-start">
          {contact.numbers.map((num) => (
            <div key={num.type} className="flex w-full items-start gap-6">
              <p className="w-10 shrink-0 text-[16px] font-normal leading-[44px] text-clearlab-steel">
                {numberLabel(num.type)}
              </p>
              <p className="flex-1 text-[16px] font-normal leading-[44px] text-clearlab-midnight">
                {num.value}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Animated check-circle icon — figma node 99:1014 (`viewBox="0 0 56 56"`,
 * 4px stroke, `clearlab-moss`). The circle and check are rendered as
 * separate `<path>` elements so each can animate on its own timeline:
 *
 *   1. circle: starts at the top of the circle (12 o'clock) and draws
 *              counter-clockwise — top → 9 → 6 → 3 → top — over 400ms.
 *              Easing is a strong cubic-in so the line crawls at first
 *              and accelerates into the second half — gives the snappy
 *              "starts slow, finishes fast" feel.
 *   2. check:  `M17.5 28 L24.5 35 L38.5 21` — starts at the upper-LEFT
 *              point of the V, through the bottom corner, ends at the
 *              upper-right tip. Drains over 250ms (after a 400ms delay)
 *              with a smooth cubic-out so the trailing tip lands gently.
 *
 * Both paths use `pathLength="1"` so the dash math is normalised to 0–1.
 * The dasharray is `1 2` (dash = 100% of path, gap = 200% of path) — a
 * single `1 1` would put the pattern boundary at the path's exact end at
 * `dashoffset: 1`, which leaves the round line cap rendering a tiny
 * green disc at the upper-right tip. Pushing the gap to 2 puts the path
 * end safely inside the gap, so nothing shows in the hidden state.
 *
 * Total icon animation: 650ms.
 */
const CIRCLE_EASE = 'cubic-bezier(0.55, 0, 0.85, 0.05)'; // strong ease-in
const CHECK_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'; // smooth ease-out

interface CheckCircleProps extends SVGProps<SVGSVGElement> {
  animated: boolean;
}

function CheckCircle({ animated, className, ...rest }: CheckCircleProps) {
  return (
    <svg
      viewBox="0 0 56 56"
      fill="none"
      aria-hidden
      focusable={false}
      className={cn('size-14 text-clearlab-moss', className)}
      {...rest}
    >
      <path
        d="M27.9998 4.66699C15.1132 4.66699 4.6665 15.1137 4.6665 28.0003C4.6665 40.887 15.1132 51.3337 27.9998 51.3337C40.8865 51.3337 51.3332 40.887 51.3332 28.0003C51.3332 15.1137 40.8865 4.66699 27.9998 4.66699Z"
        stroke="currentColor"
        strokeWidth={4}
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength={1}
        style={{
          strokeDasharray: '1 2',
          strokeDashoffset: animated ? 0 : 1,
          transition: `stroke-dashoffset 500ms ${CIRCLE_EASE}`,
        }}
      />
      <path
        d="M17.4998 28.0003L24.4998 35.0003L38.4998 21.0003"
        stroke="currentColor"
        strokeWidth={4}
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength={1}
        style={{
          strokeDasharray: '1 2',
          strokeDashoffset: animated ? 0 : 1,
          transition: `stroke-dashoffset 750ms 500ms ${CHECK_EASE}`,
        }}
      />
    </svg>
  );
}

interface ConfirmBoxProps {
  /** Whether the icon has moved up to its natural position and the body +
   *  button are visible. */
  isOpen: boolean;
  /** Whether the check-circle icon should play its draw-in animation. */
  iconAnimated: boolean;
  /** When true the icon, body, and button fade out independently (and
   *  faster than the surrounding box border) so the box feels like it's
   *  emptying out before the border catches up. */
  dismissing?: boolean;
  onReset: () => void;
}

const BOX_PADDING_Y = 40; // py-10
const ICON_SIZE = 56; // size-14
const ICON_NATURAL_CENTER_FROM_TOP = BOX_PADDING_Y + ICON_SIZE / 2; // 68

/**
 * Success card shown after the contact form is submitted (figma node
 * 99:1008). The card stays at its full natural height for the entire
 * lifecycle — body + button are always rendered and just toggle opacity,
 * so nothing slides on screen.
 *
 * The icon is moved with `transform: translateY` only:
 *   - `isOpen=false` → icon is translated down by `(boxHeight - 56) / 2 - 40`
 *     so its centre lines up with the box's vertical centre.
 *   - `isOpen=true`  → icon translates back to `translateY(0)` (its natural
 *     top-of-cluster position).
 * The transform animates over 500ms ease-out, so the icon visibly "moves
 * up" as the body + button fade in beneath it. A `ResizeObserver` keeps
 * the centred offset accurate when the body text wraps differently across
 * breakpoints.
 */
// Smooth cubic ease-in — same family as the circle's ease-in curve, just
// a touch gentler. Slow start, gathering momentum into the final position.
const ICON_SLIDE_EASE = 'cubic-bezier(0.32, 0, 0.67, 0)';

function ConfirmBox({ isOpen, iconAnimated, dismissing = false, onReset }: ConfirmBoxProps) {
  const { t } = useTranslation();
  const boxRef = useRef<HTMLDivElement>(null);
  const [centeredOffset, setCenteredOffset] = useState(0);

  useLayoutEffect(() => {
    if (!boxRef.current) return;
    const update = () => {
      if (!boxRef.current) return;
      const offset = boxRef.current.offsetHeight / 2 - ICON_NATURAL_CENTER_FROM_TOP;
      setCenteredOffset(Math.max(0, offset));
    };
    const observer = new ResizeObserver(update);
    observer.observe(boxRef.current);
    update();
    return () => observer.disconnect();
  }, []);

  // Body/button opacity follow `isOpen` normally; when dismissing they
  // collapse to opacity-0 with a 200ms transition (fast — the user wants
  // the contents gone well before the border has finished fading).
  // When `isOpen` flips true, hold for the icon's 500ms slide-up plus a
  // 100ms pause (delay 600ms) before easing the body + button in.
  const bodyOpacityClass = dismissing
    ? 'opacity-0 transition-opacity duration-[200ms] ease-out'
    : cn(
        'transition-opacity duration-[450ms] ease-out',
        isOpen ? 'opacity-100 delay-[600ms]' : 'opacity-0',
      );

  return (
    <div
      ref={boxRef}
      className="flex w-full flex-col items-center rounded-lg border border-clearlab-divider-darker px-8 py-10"
    >
      <div
        style={{
          transform: isOpen ? 'translateY(0)' : `translateY(${centeredOffset}px)`,
          opacity: dismissing ? 0 : 1,
          // transform: smooth cubic ease-in for the slide-up.
          // opacity:   short ease-out used only when `dismissing` flips true.
          transition: `transform 500ms ${ICON_SLIDE_EASE}, opacity 200ms ease-out`,
        }}
      >
        <CheckCircle animated={iconAnimated} />
      </div>

      <p
        className={cn(
          'mx-auto mt-6 w-full max-w-[406px] text-center text-[16px] font-normal leading-6 text-clearlab-midnight',
          bodyOpacityClass,
        )}
      >
        {t('contact.form.confirmText')}
      </p>

      <div className={cn('mt-10', bodyOpacityClass)}>
        <Button
          type="button"
          onClick={onReset}
          variant="transparent"
          size="small"
          tone="black"
          iconRight
        >
          {t('contact.form.confirmReset')}
        </Button>
      </div>
    </div>
  );
}

type Phase =
  | 'form'
  | 'submit-fade'
  | 'confirm-resizing'
  | 'confirm-revealed'
  | 'confirm-drawing'
  | 'confirm-shown'
  | 'reset-fade';

const FORM_FADE_MS = 450;
// Container height transition between form-height and confirm-full-height.
const HEIGHT_TRANSITION_MS = 750;
// Time spent in `confirm-revealed` — the 450ms box fade-in, then the
// ~300ms pause requested by the design before the icon starts drawing.
const REVEAL_HOLD_MS = 750;
// Icon draw budget: 500ms circle + 750ms check + a couple of frames of slack.
const ICON_DRAW_MS = 1300;
// `reset-fade` window — enough for the box opacity fade (450ms) plus the
// faster reset-only container height transition back to form-height (600ms).
const RESET_FADE_MS = 600;

const INITIAL_FIELDS = {
  fullName: '',
  email: '',
  subject: '',
  message: '',
  region: undefined as string | undefined,
  // Honeypot — hidden from real users; bots that auto-fill every field trip it
  // and the server silently drops the submission. Must stay empty.
  company: '',
};

/** No-op handler for the off-screen mirror copy of `ConfirmBox`. */
function noopReset() {
  /* unreachable — mirror is invisible and pointer-events: none */
}

/**
 * Contact form (figma node 72:4850). After a successful submit the form
 * fades out and the success card (figma node 99:1008) animates in:
 *
 *   1. submit-fade       — fields opacity 1 → 0 (300ms ease-out).
 *   2. confirm-resizing  — container height transitions form-height →
 *                           confirm-full-height (500ms). The card is
 *                           mounted but invisible (opacity 0) so only the
 *                           empty section is seen resizing.
 *   3. confirm-revealed  — card opacity 0 → 1 (300ms), then a ~200ms pause.
 *                           The card border, the (still-invisible) icon,
 *                           and the (still-invisible) body+button all sit
 *                           in their final layout positions. The icon is
 *                           translated to the box's vertical centre via
 *                           `transform: translateY`.
 *   4. confirm-drawing   — circle draws counter-clockwise from the top of
 *                           the circle (400ms), then the check draws left
 *                           tip → right tip (250ms).
 *   5. confirm-shown     — icon's transform animates back to translateY(0),
 *                           sliding it up to the top of the card. Body and
 *                           button fade in beneath it — no height collapse,
 *                           nothing slides.
 *
 * Clicking "Write another enquiry" reverses cleanly: card fades out while
 * the container resizes back to form-height; once the card is hidden the
 * fields fade in from opacity 0.
 *
 * Heights come from refs — `formRef.scrollHeight` for the form, and an
 * off-screen mirror copy of the confirm card for the expanded card height.
 * Mirror inherits the column's width so its measurement stays accurate at
 * every breakpoint.
 */
// Pragmatic email check: matches anything shaped like `local@domain.tld`.
// Intentionally lenient — full RFC 5322 isn't useful client-side; the server
// is the authoritative validator. This catches typos like `name@gmail` or
// `name&gmail.com` (the figma example), not edge-case valid-but-weird addresses.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactForm() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('form');
  const [fields, setFields] = useState(INITIAL_FIELDS);
  // `submitAttempted` flips true the first time the user clicks Send with
  // any invalid field — after that, email-format errors are shown live so
  // the user gets immediate feedback as they correct. Resets when the form
  // resets via the success-card "write another" flow.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Network state for the actual submit to /api/contact.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const regionOrder = t('contact.form.regionOrder', { returnObjects: true }) as string[];
  const regionOptions = regionOrder.map((value) => ({
    value,
    label: t(`contact.form.regions.${value}`),
  }));

  const formRef = useRef<HTMLFormElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [formHeight, setFormHeight] = useState(0);
  const [confirmFullHeight, setConfirmFullHeight] = useState(0);

  // Measure the form's natural height. Re-measures on viewport resize —
  // textarea wrapping, dropdown menu changes, font-size changes etc.
  useLayoutEffect(() => {
    if (!formRef.current) return;
    const update = () => {
      if (formRef.current) setFormHeight(formRef.current.scrollHeight);
    };
    const observer = new ResizeObserver(update);
    observer.observe(formRef.current);
    update();
    return () => observer.disconnect();
  }, []);

  // Measure the confirm card's expanded height via the off-screen mirror.
  useLayoutEffect(() => {
    if (!measureRef.current) return;
    const update = () => {
      if (measureRef.current) setConfirmFullHeight(measureRef.current.scrollHeight);
    };
    const observer = new ResizeObserver(update);
    observer.observe(measureRef.current);
    update();
    return () => observer.disconnect();
  }, []);

  // Phase orchestration — kicks the next transition once the current one
  // has had time to play out.
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (phase === 'submit-fade') {
      timeout = setTimeout(() => setPhase('confirm-resizing'), FORM_FADE_MS);
    } else if (phase === 'confirm-resizing') {
      timeout = setTimeout(() => setPhase('confirm-revealed'), HEIGHT_TRANSITION_MS);
    } else if (phase === 'confirm-revealed') {
      timeout = setTimeout(() => setPhase('confirm-drawing'), REVEAL_HOLD_MS);
    } else if (phase === 'confirm-drawing') {
      timeout = setTimeout(() => setPhase('confirm-shown'), ICON_DRAW_MS);
    } else if (phase === 'reset-fade') {
      timeout = setTimeout(() => setPhase('form'), RESET_FADE_MS);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [phase]);

  const emailFormatValid = EMAIL_PATTERN.test(fields.email.trim());
  // Button is enabled as soon as every required field has *something* in it —
  // including email, regardless of format. That lets the user click Send
  // with a typo'd address and discover the format problem via the inline
  // error rather than silently bumping into a disabled button.
  const canSubmit =
    fields.fullName.trim() !== '' &&
    fields.email.trim() !== '' &&
    fields.message.trim() !== '' &&
    fields.region !== undefined;
  // Actual submission still requires a well-formed email. If the user
  // clicks Send with a typo'd address, `submitAttempted` flips on and the
  // error appears, but `isValid` stays false so the phase machine doesn't
  // advance.
  const isValid = canSubmit && emailFormatValid;
  // Email error renders only after the first submit attempt — and only when
  // an email is typed but doesn't match the format. Empty stays a generic
  // required-field nudge from the disabled submit, not an inline error
  // (matches the figma example which shows the message for a typo'd input).
  const emailError =
    submitAttempted && fields.email.trim() !== '' && !emailFormatValid
      ? t('contact.form.errors.invalidEmail')
      : undefined;

  // The confirm card stays at its full natural height for its entire
  // lifecycle, so the container height only ever swaps between form-height
  // and confirm-full-height — no intermediate icon-only-height step.
  const containerHeight = (() => {
    switch (phase) {
      case 'form':
      case 'submit-fade':
      case 'reset-fade':
        return formHeight;
      case 'confirm-resizing':
      case 'confirm-revealed':
      case 'confirm-drawing':
      case 'confirm-shown':
        return confirmFullHeight || formHeight;
    }
  })();

  // Form fades in only once `reset-fade` has finished — i.e. once the
  // confirm box has fully faded out and the container has finished resizing
  // back to form-height. Keeping `reset-fade` form-opacity at 0 ensures the
  // two animations don't visually overlap and step on each other.
  const formOpacity = phase === 'form' ? 1 : 0;
  // Mounted from the moment the section starts resizing through to the
  // tail of the reset fade — covers every phase where the card occupies
  // the absolutely-positioned overlay slot.
  const confirmMounted =
    phase === 'confirm-resizing' ||
    phase === 'confirm-revealed' ||
    phase === 'confirm-drawing' ||
    phase === 'confirm-shown' ||
    phase === 'reset-fade';
  // Card stays invisible during `confirm-resizing` (so only the empty
  // section is seen resizing) and during `reset-fade` (so it fades out).
  const confirmOpacity =
    phase === 'confirm-revealed' || phase === 'confirm-drawing' || phase === 'confirm-shown'
      ? 1
      : 0;
  const iconAnimated = phase === 'confirm-drawing' || phase === 'confirm-shown';
  const cardOpen = phase === 'confirm-shown';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Guard against re-entry while a request is in flight (double-click /
    // double-submit) or while the success animation is already playing.
    if (phase !== 'form' || submitting) return;
    // Always flag the attempt so the email field can surface its format
    // error live for any subsequent edits — even if the button is also in
    // the disabled state due to an empty required field elsewhere.
    setSubmitAttempted(true);
    if (!isValid) return;

    setSubmitError(undefined);
    setSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fields.fullName,
          email: fields.email,
          subject: fields.subject,
          message: fields.message,
          // Region drives the destination inbox server-side.
          region: fields.region,
          // Honeypot — real users leave this empty.
          company: fields.company,
        }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      // Only play the success card once the email actually sent.
      setPhase('submit-fade');
    } catch {
      setSubmitError(t('contact.form.errors.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFields(INITIAL_FIELDS);
    setSubmitAttempted(false);
    setPhase('reset-fade');
  };

  return (
    <div className="relative w-full">
      <div
        className={cn(
          'relative w-full transition-[height] ease-out',
          // The reset path is intentionally 150ms snappier than the
          // forward path so clicking "Write another" feels responsive.
          phase === 'reset-fade' ? 'duration-[600ms]' : 'duration-[750ms]',
        )}
        style={{ height: containerHeight ? `${containerHeight}px` : 'auto' }}
      >
        <form
          ref={formRef}
          noValidate
          onSubmit={(e) => void handleSubmit(e)}
          className={cn(
            'absolute inset-x-0 top-0 flex w-full flex-col items-start gap-10',
            'transition-opacity duration-[450ms] ease-out',
            formOpacity === 0 && 'pointer-events-none',
          )}
          style={{ opacity: formOpacity }}
          aria-hidden={formOpacity === 0}
        >
          <div className="flex w-full flex-col items-start gap-4">
            <TextField
              name="fullName"
              autoComplete="name"
              required
              label={t('contact.form.fullName')}
              placeholder={t('contact.form.placeholders.fullName')}
              value={fields.fullName}
              onChange={(value) => setFields((f) => ({ ...f, fullName: value }))}
            />
            <TextField
              name="email"
              type="email"
              autoComplete="email"
              required
              label={t('contact.form.email')}
              placeholder={t('contact.form.placeholders.email')}
              value={fields.email}
              onChange={(value) => setFields((f) => ({ ...f, email: value }))}
              error={emailError}
            />
            <TextField
              name="subject"
              label={t('contact.form.subject')}
              placeholder={t('contact.form.placeholders.subject')}
              value={fields.subject}
              onChange={(value) => setFields((f) => ({ ...f, subject: value }))}
            />
            <TextField
              multiline
              rows={10}
              name="message"
              required
              label={t('contact.form.message')}
              placeholder={t('contact.form.placeholders.message')}
              value={fields.message}
              onChange={(value) => setFields((f) => ({ ...f, message: value }))}
            />
            <Dropdown
              name="region"
              required
              placeholder={t('contact.form.regionLabel')}
              menuLabel={t('contact.form.regionMenuLabel')}
              options={regionOptions}
              value={fields.region}
              onChange={(value) => setFields((f) => ({ ...f, region: value }))}
              triggerClassName="rounded-[16px]"
            />
            {/* Honeypot: hidden from users (off-screen, untabbable, hidden from
                AT) but visible to naive bots that fill every field. */}
            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={fields.company}
              onChange={(e) => setFields((f) => ({ ...f, company: e.target.value }))}
              className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
            />
          </div>

          <div className="flex w-full flex-col items-start gap-2">
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              aria-disabled={!canSubmit || submitting}
              aria-busy={submitting}
              className={cn(
                'flex w-full items-center justify-center gap-2 rounded-[16px] px-6 py-4',
                'text-[16px] font-semibold leading-[18px] text-clearlab-oat',
                'transition-colors duration-fast ease-in',
                canSubmit && !submitting
                  ? 'bg-clearlab-blue hover:bg-clearlab-blue/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                  : 'cursor-not-allowed bg-clearlab-divider-darker',
              )}
            >
              {submitting ? t('contact.form.submitting') : t('contact.form.submit')}
            </button>
            {submitError ? (
              <p role="alert" className="text-[14px] leading-5 text-[#A50000]">
                {submitError}
              </p>
            ) : null}
          </div>
        </form>

        {confirmMounted ? (
          <div
            className={cn(
              'absolute inset-x-0 top-0 transition-opacity duration-[450ms] ease-out',
              confirmOpacity === 0 && 'pointer-events-none',
            )}
            style={{ opacity: confirmOpacity }}
            role="status"
            aria-live="polite"
          >
            <ConfirmBox
              isOpen={cardOpen}
              iconAnimated={iconAnimated}
              dismissing={phase === 'reset-fade'}
              onReset={handleReset}
            />
          </div>
        ) : null}
      </div>

      {/* Off-screen mirror — used only to pre-measure the confirm card's
          fully-expanded natural height so the container can transition
          smoothly to that exact value when the card opens. Inherits the
          column width via `inset-x-0` so the measurement is accurate at
          every breakpoint. */}
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute inset-x-0 top-0"
      >
        <ConfirmBox isOpen iconAnimated={false} onReset={noopReset} />
      </div>
    </div>
  );
}
